import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { startMailboxPolling } from '../utils/mailboxPolling';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function normalizeText(value) {
  return String(value || '').trim();
}

function getLogoUrl(logoUrl) {
  if (!logoUrl) return '';
  if (/^https:\/\//i.test(logoUrl)) return logoUrl;
  if (logoUrl.startsWith('/api/')) return logoUrl;
  return `${API_URL}${logoUrl}`;
}

function getSizingText(name) {
  return normalizeText(name)
    .replace(/\s*\((haftungsbeschr[aä]nkt|haftungsbeschraenkt)\)\s*/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function splitDisplayName(name) {
  const text = normalizeText(name);
  const match = text.match(/^(.*)\s*(\((haftungsbeschr[aä]nkt|haftungsbeschraenkt)\))\s*$/i);
  if (!match) {
    return { mainName: text, legalSuffix: '' };
  }

  return {
    mainName: match[1].trim(),
    legalSuffix: match[2],
  };
}

function getNameFontSize(name, count) {
  const text = getSizingText(name);
  const length = text.length;
  const longestWord = text.split(/\s+/).reduce((max, word) => Math.max(max, word.length), 0);

  let size = 22;
  if (count > 12) size = 18;
  if (count > 48) size = 16;
  if (count > 80) size = 13;

  if (length > 32) size -= 1;
  if (length > 48) size -= 1;
  if (longestWord > 18) size -= 1;
  if (longestWord > 22) size -= 1;

  if (count > 80) return Math.max(10, size);
  if (count > 48) return Math.max(12, size);
  return Math.max(14, size);
}

function MailboxDisplay() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [items, setItems] = useState([]);
  const [updatedAt, setUpdatedAt] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const displayClassName = useMemo(() => {
    const count = items.length;
    if (count <= 12) return 'mailbox-display-grid mailbox-display-grid--roomy';
    if (count <= 48) return 'mailbox-display-grid mailbox-display-grid--standard';
    if (count <= 80) return 'mailbox-display-grid mailbox-display-grid--many';
    return 'mailbox-display-grid mailbox-display-grid--ultra';
  }, [items.length]);

  const shellClassName = useMemo(() => {
    const count = items.length;
    if (count > 80) return 'mailbox-display-shell mailbox-display-shell--ultra';
    return 'mailbox-display-shell';
  }, [items.length]);

  useEffect(() => {
    setItems([]);
    setUpdatedAt('');
    setError('');
    setLoading(true);
    if (!token) {
      setError('Display-Token fehlt.');
      setLoading(false);
      return undefined;
    }
    return startMailboxPolling({
      url: `${API_URL}/display/mailbox?token=${encodeURIComponent(token)}`,
      onData: (payload) => {
        setItems(payload.items.filter((item) => item && normalizeText(item.display_name)));
        setUpdatedAt(payload.updated_at || new Date().toISOString());
      },
      onError: setError,
      onLoading: setLoading,
      onExpire: () => setItems([]),
    });
  }, [token]);

  return (
    <main className={shellClassName}>
      <header className="mailbox-display-header">
        <div>
          <p>YourGermanAddress</p>
          <h1>Briefkasten</h1>
        </div>
        <span>{items.length} Einträge</span>
      </header>

      {loading ? (
        <div className="mailbox-display-state">Lädt...</div>
      ) : error && items.length === 0 ? (
        <div className="mailbox-display-state mailbox-display-state--error">{error}</div>
      ) : items.length === 0 ? (
        <div className="mailbox-display-state">Aktuell sind keine Namen hinterlegt.</div>
      ) : (
        <section className={displayClassName}>
          {items.map((item, index) => {
            const logoUrl = getLogoUrl(item.logo_url);
            const nameFontSize = getNameFontSize(item.display_name, items.length);
            const { mainName, legalSuffix } = splitDisplayName(item.display_name);
            return (
              <article
                className="mailbox-display-card"
                key={`${item.display_name}-${index}`}
                style={{ '--mailbox-name-size': `${nameFontSize}px` }}
              >
                {logoUrl ? (
                  <img src={logoUrl} alt="" className="mailbox-display-logo" />
                ) : (
                  <div className="mailbox-display-logo-placeholder">
                    {item.display_name.slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="mailbox-display-name">
                  <span>{mainName}</span>
                  {legalSuffix ? <small>{legalSuffix}</small> : null}
                </div>
              </article>
            );
          })}
        </section>
      )}

      <footer className="mailbox-display-footer">
        {error && items.length > 0 ? <span role="status">Aktualisierung verzögert – erneuter Versuch läuft automatisch.</span> : null}
        <span>
          Letzte Aktualisierung:{' '}
          {updatedAt ? new Date(updatedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '-'}
        </span>
      </footer>
    </main>
  );
}

export default MailboxDisplay;
